import React from 'react';
import { SelectOption } from 'global';
import { Add, DeleteOutline } from '@mui/icons-material';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { SelectField, CurrencyInputField } from 'components/form';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { OptionFormValues } from '../../model';
import { useEquipmentAttributes } from '../../hooks';

interface Props {
  accountId: string;
}

const ANY_OPTION: SelectOption<string> = { label: 'Any', value: '' };

const withAny = (options: SelectOption<string>[], current?: string | null): SelectOption<string>[] => {
  const next = [ANY_OPTION, ...options];

  if (current && !next.some(option => option.value === current)) {
    next.push({ label: current, value: current });
  }

  return next;
};

export const OptionPricingTable = ({ accountId }: Props) => {
  const { control, setValue } = useFormContext<OptionFormValues>();
  const { fields, append, remove } = useFieldArray({ control, name: 'pricing' });

  const rows = useWatch({ control, name: 'pricing' }) ?? [];

  const { getYearOptions, getMakeOptions, getModelOptions, getModelBaseOptions } = useEquipmentAttributes(accountId);

  const addRow = () => append({ year: '', make: '', model: '', modelBase: '', cost: 0 });

  return (
    <Stack gap={1.5}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1" fontWeight={600}>
          Pricing
        </Typography>
        <Button size="small" variant="outlined" startIcon={<Add />} onClick={addRow}>
          Add Row
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary">
        Leave a column as &quot;Any&quot; to apply the price broadly. Only cost is required.
      </Typography>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 120 }}>Year</TableCell>
              <TableCell sx={{ minWidth: 160 }}>Make</TableCell>
              <TableCell sx={{ minWidth: 160 }}>Model</TableCell>
              <TableCell sx={{ minWidth: 160 }}>Base Model</TableCell>
              <TableCell sx={{ minWidth: 140 }}>Cost</TableCell>
              <TableCell sx={{ width: 56 }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {fields.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    No pricing rows yet. Click &quot;Add Row&quot; to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {fields.map((field, index) => {
              const row = rows[index] ?? {};

              return (
                <TableRow key={field.id}>
                  <TableCell>
                    <SelectField
                      control={control}
                      name={`pricing.${index}.year`}
                      options={withAny(getYearOptions(), row.year)}
                    />
                  </TableCell>

                  <TableCell>
                    <SelectField
                      control={control}
                      name={`pricing.${index}.make`}
                      options={withAny(getMakeOptions(), row.make)}
                      extraAction={() => {
                        setValue(`pricing.${index}.model`, '');
                        setValue(`pricing.${index}.modelBase`, '');
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <SelectField
                      control={control}
                      name={`pricing.${index}.model`}
                      options={withAny(getModelOptions({ make: row.make }), row.model)}
                      extraAction={() => {
                        setValue(`pricing.${index}.modelBase`, '');
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <SelectField
                      control={control}
                      name={`pricing.${index}.modelBase`}
                      options={withAny(getModelBaseOptions({ make: row.make, model: row.model }), row.modelBase)}
                    />
                  </TableCell>

                  <TableCell>
                    <CurrencyInputField control={control} name={`pricing.${index}.cost`} />
                  </TableCell>

                  <TableCell>
                    <Tooltip title="Remove Row">
                      <IconButton size="small" color="error" onClick={() => remove(index)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Stack>
  );
};
